/// <amd-module name="Artifacts/Pypi/redux/Sagas" />

import { PackageActions } from "Artifacts/Package/redux/Actions";
import {
  BatchGenerator,
  IBatchIterator,
  TransformingBatchGenerator,
} from "Artifacts/Package/services/BatchHandling";
import { PypiActionTypes } from "Artifacts/Pypi/redux/Actions";
import { IFeedPyPiDataService } from "Artifacts/Pypi/services/PyPiDataService";
import { PerfScenarios } from "Artifacts/Root/Constants";
import { FeedPerformanceHelper } from "Artifacts/Root/helpers/FeedPerformanceHelper";
import {
  IDeleteActionPayload,
  IPromoteActionPayload,
  IRestoreActionPayload,
  IUpstreamOverrideActionPayload,
} from "Artifacts/Root/Protocols/types/ICommandActionPayloads";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IPackageVersionIdentity } from "Artifacts/Root/types";
import {
  MinimalPackageDetails,
  UpstreamingBehavior,
  UpstreamVersionVisibility,
} from "Artifacts/Root/types/PackagingShared";
import { SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssScenario } from "VSS/Platform/Performance";

export function* rootSaga(): SagaIterator {
  yield takeEvery(PypiActionTypes.Promote, promote);
  yield takeEvery(PypiActionTypes.SoftDelete, softDelete);
  yield takeEvery(PypiActionTypes.Restore, restore);
  yield takeEvery(PypiActionTypes.PermanentDelete, permanentDelete);
  yield takeEvery(PypiActionTypes.SetUpstreamOverride, setUpstreamOverride);
}

export function* promote(
  action: ActionWithPayload<PypiActionTypes.Promote, IPromoteActionPayload>,
): Generator {
  try {
    yield put(RootActions.promoteCommandStarted());
    const { feed, versions, viewId, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedPyPiDataService>("IFeedPyPiDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchPromote], projectId, feed.id, viewId, iterator);

    // retrieve the view so the name can be applied on the selected versions.
    const view = feed.views.find((v) => v.id === viewId);

    yield put(RootActions.promoteCommandCompleted());
    yield call(onSuccessCallback, versions, view?.name);
  } catch (error) {
    yield put(RootActions.promoteCommandFailed(error.message));
  }
}

export function* softDelete(
  action: ActionWithPayload<PypiActionTypes.SoftDelete, IDeleteActionPayload>,
): Generator {
  try {
    yield put(RootActions.softDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedPyPiDataService>("IFeedPyPiDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (version: IPackageVersionIdentity) => {
        return {
          id: version.name,
          version: version.version,
        };
      },
    );
    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchDelete], projectId, feed.id, iterator);

    yield put(RootActions.softDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    yield put(RootActions.softDeleteCommandFailed(error.message));
  }
}

export function* restore(
  action: ActionWithPayload<PypiActionTypes.Restore, IRestoreActionPayload>,
): Generator {
  yield put(RootActions.restoreCommandStarted());
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionRestore
        : PerfScenarios.MultiplePackageVersionsRestoredToFeed,
    );

    const dataService = pageContext.getService<IFeedPyPiDataService>("IFeedPyPiDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (packageVersion: IPackageVersionIdentity) => {
        return {
          id: packageVersion.name,
          version: packageVersion.version,
        };
      },
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchRestoreToFeed], projectId, feed.id, iterator);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.restoreCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.restoreCommandFailed(error.message));
  }
}

export function* permanentDelete(
  action: ActionWithPayload<PypiActionTypes.PermanentDelete, IDeleteActionPayload>,
): Generator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    yield put(RootActions.permDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionPermanentDelete
        : PerfScenarios.MultiplePackageVersionsPermanentDeleted,
    );

    const dataService = pageContext.getService<IFeedPyPiDataService>("IFeedPyPiDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchPermanentlyDelete], projectId, feed.id, iterator);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.permDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.permDeleteCommandFailed(error.message));
  }
}

export function* setUpstreamOverride(
  action: ActionWithPayload<PypiActionTypes.SetUpstreamOverride, IUpstreamOverrideActionPayload>,
): Generator {
  try {
    yield put(PackageActions.upstreamOverrideCommandStarted());
    const { feed, packageName, allowExternalVersions, onSuccessCallback } = action.payload;

    const behavior = {
      versionsFromExternalUpstreams: allowExternalVersions
        ? UpstreamVersionVisibility.AllowExternalVersions
        : UpstreamVersionVisibility.Auto,
    } as UpstreamingBehavior;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;

    const dataService = pageContext.getService<IFeedPyPiDataService>("IFeedPyPiDataService");

    const projectId = feed.project ? feed.project.id : "";

    yield call(
      [dataService, dataService.setUpstreamingBehavior],
      feed.id,
      packageName,
      behavior,
      projectId,
    );

    yield put(PackageActions.upstreamOverrideCommandCompleted(allowExternalVersions));
    yield call(onSuccessCallback);
  } catch (error) {
    yield put(PackageActions.upstreamOverrideCommandFailed(error.message));
  }
}
